import React from "react";
import { useNavigate } from "react-router-dom";

// Agent Radar — shortlist (snapshot) chat card (radar_shortlist_share attachment).
// Link-preview family of the sibling player card (RadarPlayerShareCard): same
// 296px white card chrome (#E4E9F0 border, 14px radius, soft shadow) rendered as
// a bubble in the message stream, sent and received alike. Anatomy from the
// design source "Radar Chat Cards.dc.html" frames 1b + 2a:
//   "SHORTLIST" kicker · list name · "N players" · overlapping avatar row
//   (max 4 + "+N" overflow chip) · "Snapshot · shared {date}" line ·
//   solid-blue "View shortlist →" CTA → the existing read-only snapshot view.
//
// Metadata is read tolerantly so small BE field-name drift never blanks the card.
// avatars: each item is a URL string OR { player_image_url|image_url|url, name }.
// Edge state (frame 1c-b, "unavailable target"): when no snapshot id is present
// the CTA is disabled with a quiet note instead of a dead link — never an empty
// action row.
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AvatarChip = ({ item, index }) => {
  const url =
    typeof item === "string"
      ? item
      : item?.player_image_url || item?.image_url || item?.url || "";
  const name = typeof item === "string" ? "" : item?.name || "";
  const base =
    "w-[30px] h-[30px] rounded-full border-2 border-white overflow-hidden flex-shrink-0";
  const style = index === 0 ? {} : { marginLeft: -9 };
  if (url) {
    return (
      <div className={base} style={style}>
        <img src={url} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div
      className={`${base} bg-[#E8F1FF] text-[#1B6FE0] text-[10px] font-semibold flex items-center justify-center`}
      style={style}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </div>
  );
};

const RadarShortlistShareCard = (props) => {
  const navigate = useNavigate();
  const meta = props.attachments?.[0]?.metadata || {};

  const listName = meta.list_name || meta.name || "Shortlist";
  const count = meta.count ?? (Array.isArray(meta.avatars) ? meta.avatars.length : 0);
  const snapshotId = meta.snapshot_id ?? meta.snapshotId ?? meta.id;
  const allAvatars = Array.isArray(meta.avatars) ? meta.avatars : [];
  const avatars = allAvatars.slice(0, 4);
  const overflow = Math.max(0, count - avatars.length);
  const sharedDate = formatDate(meta.shared_at || meta.shared_on || meta.shared_date || meta.created_at);
  // Route to OUR registered snapshot view by id. The BE's suggested `cta_path`
  // uses a different string, so snapshot_id is the contract — not the path.
  const ctaPath = snapshotId != null ? `/agent/radar/snapshots/${snapshotId}` : null;

  return (
    <div className="w-[296px] bg-white border border-[#E4E9F0] rounded-[14px] shadow-[0_1px_3px_rgba(0,0,0,0.06)] p-[13px]">
      <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#9CA3AF]">
        Shortlist
      </div>
      <div className="text-[15px] font-bold text-[#111827] mt-[3px] truncate">{listName}</div>
      <div className="text-[11.5px] text-[#6B7280] mt-px">
        {count} player{count === 1 ? "" : "s"}
      </div>

      {avatars.length > 0 && (
        <div className="flex mt-[10px]">
          {avatars.map((a, i) => (
            <AvatarChip key={i} item={a} index={i} />
          ))}
          {overflow > 0 && (
            <div
              className="w-[30px] h-[30px] rounded-full border-2 border-white bg-[#EEF2F7] text-[#6B7280] text-[10px] font-semibold flex items-center justify-center flex-shrink-0"
              style={{ marginLeft: avatars.length ? -9 : 0 }}
              aria-hidden="true"
            >
              +{overflow}
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-[#9CA3AF] mt-[9px]">
        Snapshot{sharedDate ? ` · shared ${sharedDate}` : ""}
      </div>

      {ctaPath ? (
        <button
          type="button"
          onClick={() => navigate(ctaPath)}
          className="w-full mt-[11px] min-h-[44px] bg-[#2283FF] rounded-[9px] py-2 text-[13px] font-semibold text-white hover:bg-[#1B6FE0] cursor-pointer"
        >
          View shortlist →
        </button>
      ) : (
        <>
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="w-full mt-[11px] min-h-[44px] bg-[#EEF2F7] rounded-[9px] py-2 text-[13px] font-semibold text-[#9CA3AF] cursor-not-allowed"
          >
            View shortlist
          </button>
          <div className="text-[11px] text-[#9CA3AF] mt-[7px] leading-[1.4]">
            This shortlist is no longer available.
          </div>
        </>
      )}
    </div>
  );
};

export default RadarShortlistShareCard;
